import {
    Box,
    ClickAwayListener,
    IconButton,
    Paper,
    Popper,
} from '@mui/material';
import { Repeat } from 'lucide-react';
import { useRef, useState } from 'react';
import AvailableImageItem from '~/components/ImageItem/AvailableImageItem';
import { AvailableImage } from '~/hooks/image/usePrimaryImage';

interface ImageChooserButtonProps {
    images: AvailableImage[];
    selectedSeriesUid?: string;
    onChoose: (image: AvailableImage) => void;
    ariaLabel: string;
    testId?: string;
}

export default function ImageChooserButton({
    images,
    selectedSeriesUid,
    onChoose,
    ariaLabel,
    testId,
}: ImageChooserButtonProps) {
    const [isOpen, setIsOpen] = useState(false);
    const anchorRef = useRef<HTMLButtonElement>(null);

    const choices = images.filter(
        (image) => image.seriesInstanceUID !== selectedSeriesUid
    );
    if (choices.length === 0) {
        return null;
    }

    const handleClickAway = (event: MouseEvent | TouchEvent) => {
        const target = event.target as HTMLElement;
        if (
            target === document.body ||
            target.closest('[role="presentation"]') ||
            target.closest('[role="dialog"]')
        ) {
            return;
        }
        setIsOpen(false);
    };

    return (
        <>
            <IconButton
                ref={anchorRef}
                size="small"
                onClick={() => setIsOpen((open) => !open)}
                aria-label={ariaLabel}
                data-testid={testId}
                sx={{ p: 0, color: 'primary.main' }}
            >
                <Repeat size={12} />
            </IconButton>
            <Popper
                open={isOpen}
                anchorEl={anchorRef.current}
                placement="bottom-end"
                sx={{ zIndex: 1300 }}
            >
                <ClickAwayListener onClickAway={handleClickAway}>
                    <Paper
                        elevation={2}
                        sx={(theme) => ({
                            bgcolor: theme.palette.background.default,
                            border: `1px solid ${theme.palette.divider}`,
                        })}
                    >
                        <Box
                            display="flex"
                            flexDirection="column"
                            maxHeight={300}
                            minWidth={200}
                            overflow="auto"
                        >
                            {choices.map((image) => (
                                <AvailableImageItem
                                    key={image.seriesInstanceUID}
                                    image={image}
                                    onSelect={() => {
                                        onChoose(image);
                                        setIsOpen(false);
                                    }}
                                />
                            ))}
                        </Box>
                    </Paper>
                </ClickAwayListener>
            </Popper>
        </>
    );
}
